import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { isValidId } from "@hooks/use-get/utils";
import { useScreenAlert } from "@hooks/use-screen-alert";

//* Added "postType" arg for reusability
export const useDeleteAsset = (postType: string = "assets") => {
    const [loading, setLoading] = useState(false);
    const { setError } = useScreenAlert();

    const { user } = useUserContext();
    const { getApiUrl } = useMultiSiteContext();
    const { refreshUserToken } = useRefreshTokenMutation();

    const deleteAsset = useCallback(
        async (id: string | number) => {
            setLoading(true);

            // Validate ID to prevent injection attacks
            if (!isValidId(id)) {
                setError("Invalid asset ID.");
                setLoading(false);
                return;
            }

            const apiUrl = getApiUrl(`wp-json/wp/v2/${postType}/${id}`);
            try {
                const response = await fetchWithTokenRefresh(
                    apiUrl,
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${user?.authToken ?? ""}`,
                        },
                    },
                    () => refreshUserToken(user?.refreshToken ?? "")
                );

                if (!response.ok) {
                    const body = await response.json().catch(() => null);
                    const serverMessage: string | undefined = body?.message;
                    setError(serverMessage ?? "Failed to delete asset. Please try again.");
                    console.error("[useDeleteAsset] HTTP", response.status, serverMessage ?? "");
                    setLoading(false);
                    return;
                }

                setLoading(false);
                return response;
            } catch (err) {
                setError("Failed to delete asset. Please try again.");
                console.error("[useDeleteAsset]", err);
                setLoading(false);
            }
        },
        [getApiUrl, postType, user?.authToken, user?.refreshToken, refreshUserToken, setError]
    );

    return {
        deleteAsset,
        loading,
    };
};
